import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Typography, Flex, Tag, Tooltip, Table, Button, message } from 'antd';
import { UserOutlined, TeamOutlined, SafetyCertificateOutlined, DatabaseOutlined, DollarCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { getDashboard } from '@/api/dashboard';
import { useQuery } from '@tanstack/react-query';
import { OperationList } from '@/components/OperationList';
import { TokenInfo, useTotalElements, useTotalElementsByChain, useTVLData } from '@/hooks/useContractData';
import { formatUnits } from 'viem';
import { ColumnsType } from 'antd/es/table';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const dashboard = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await getDashboard();
      return res.data;
    },
  });

  const { data: totalElements, chainData } = useTotalElements();
  const { data: tvlData } = useTVLData();

  // 链元素数据表格列
  const chainElementsColumns = [
    {
      title: '链名称',
      dataIndex: 'chainName',
      key: 'chainName',
    },
    {
      title: '链ID',
      dataIndex: 'chainId',
      key: 'chainId',
    },
    {
      title: '合约地址',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (address: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Tooltip title={address}>
            <span style={{ fontFamily: 'monospace' }}>
              {address.slice(0, 6)}...{address.slice(-4)}
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Total UTXOs',
      dataIndex: 'totalElements',
      key: 'totalElements',
      render: (value: bigint) => value.toLocaleString(),
    },
  ];

  // TVL数据表格列
  const tvlColumns: ColumnsType<TokenInfo> = [
    {
      title: '链',
      dataIndex: ['chain', 'name'],
      key: 'chainName',
    },
    {
      title: '代币地址',
      dataIndex: 'token',
      key: 'token',
      render: (address: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Tooltip title={address}>
            <span style={{ fontFamily: 'monospace' }}>
              {address.slice(0, 6)}...{address.slice(-4)}
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: '合约余额',
      dataIndex: 'contractBalance',
      key: 'contractBalance',
      render: (amount: bigint, record) => <span>{formatUnits(amount, record.decimals)}</span>,
    },
    {
      title: 'Accumulated Fee',
      dataIndex: 'accumulatedFee',
      key: 'accumulatedFee',
      render: (amount: bigint, record) => <span>{formatUnits(amount, record.decimals)}</span>,
    },
    {
      title: '代币',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (symbol: string, record) => (
        <div>
          <strong>{symbol}</strong>
          <br />
          <small style={{ color: '#999' }}>{record.name}</small>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>仪表盘</Title>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="激活账号总数" value={dashboard.data?.total_users} prefix={<UserOutlined />} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="禁用账号总数" value={dashboard.data?.disabled_users} prefix={<UserOutlined />} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="系统角色总数" value={dashboard.data?.total_roles} prefix={<TeamOutlined />} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="权限项目总数" value={dashboard.data?.total_permissions} prefix={<SafetyCertificateOutlined />} valueStyle={{ color: '#13cf7b' }} />
          </Card>
        </Col>
      </Row>

      {/* 合约数据统计 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic title="Total UTXOs" value={totalElements.toLocaleString()} prefix={<DatabaseOutlined />} valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic title="Total Pools" value={tvlData?.totalPools || 0} prefix={<DollarCircleOutlined />} valueStyle={{ color: '#eb2f96' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic title="支持的代币种类" value={tvlData?.tokens.length || 0} prefix={<DollarCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      {/* 各链 Total UTXOs 详情 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title="各链 Total UTXOs 详情" size="small">
            <Table dataSource={chainData} columns={chainElementsColumns} pagination={false} size="small" rowKey="chainId" />
          </Card>
        </Col>
      </Row>

      {/* TVL 详情 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title="TVL 详情" size="small">
            <Table dataSource={tvlData?.tokens || []} columns={tvlColumns} pagination={false} size="small" rowKey={(record) => `${record.chain.id}-${record.token}`} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <OperationList />
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
